import {
  Alert,
  Anchor,
  Badge,
  Card,
  Center,
  Code,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Timeline,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowLeft } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { MetaPair } from '@/components/meta-pair';
import { PageHeader } from '@/components/page-header';
import { SectionHeading } from '@/components/section-heading';
import { ApiError, NetworkError } from '@/lib/api-error';
import { getUser, providersOf, type UserDetail } from '@/lib/users';
import classes from './users-page.module.css';

const stamp = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

function at(value: string | null): string {
  return value ? stamp.format(new Date(value)) : '—';
}

const backLink = (
  <Anchor component={Link} to="/users" size="sm">
    <Group gap={4} wrap="nowrap">
      <IconArrowLeft size={14} />
      Users
    </Group>
  </Anchor>
);

/**
 * One person, and everything the platform knows about them.
 *
 * docs/analytics.md calls this "a surveillance surface" and says its access
 * "belongs in the audit trail". **It is not in one.** No audit table exists and
 * nothing records that this page was opened, which is a gap recorded rather
 * than solved — see the same document. Until it is closed, the only thing
 * standing between this screen and everyone in the company is the Workspace
 * gate on the admin login.
 *
 * The transcript is deliberately absent. `conversation_turns` holds the first
 * learner-authored free text in this database, and the API does not serve it
 * here: what a scene was, how far it got and what it estimated are operational
 * questions; what somebody actually said is not.
 */
export function UserDetailPage() {
  const { userId } = useParams();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Unreachable through the router, which only matches this component with
      // the parameter present. Handled anyway because the alternative is a
      // spinner that never resolves, which reads as the API hanging.
      if (!userId) {
        setIsLoading(false);
        return;
      }

      try {
        const result = await getUser(userId);
        if (cancelled) return;
        setUser(result);
        setLoadError(null);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (isLoading) {
    return (
      <Stack gap="lg" className={classes.page}>
        {backLink}
        <PageHeader title="User" />
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null || user === null) {
    const missing =
      loadError instanceof ApiError && loadError.status === 404;

    return (
      <Stack gap="lg" className={classes.page}>
        {backLink}
        <PageHeader title={missing ? 'User not found' : 'User'} />
        <Alert
          color={missing ? 'gray' : 'red'}
          title={missing ? 'No such user' : 'Cannot load this user'}
          icon={missing ? undefined : <IconAlertTriangle size={18} />}
        >
          <Text size="sm">
            {missing
              ? 'Nothing matches that id. A withdrawn user is not shown here either.'
              : describeError(loadError)}
          </Text>
        </Alert>
      </Stack>
    );
  }

  const providers = providersOf(user);

  return (
    <Stack gap="xl" className={classes.page}>
      {backLink}

      <PageHeader
        title={user.email ?? user.displayName ?? 'Anonymous user'}
        description={
          user.registeredAt
            ? 'An account. The same row this device has used since its first run.'
            : 'Anonymous, and device-scoped. This is the ordinary state, not a pending one.'
        }
        actions={<Code>{user.id}</Code>}
      />

      <Card>
        <Group gap="xl" wrap="wrap">
          <MetaPair label="Name" value={user.displayName ?? '—'} />
          <MetaPair
            label="Email"
            value={
              user.email ? (
                <Group gap={6} wrap="nowrap">
                  {user.email}
                  {user.emailVerified ? (
                    <Badge variant="light" color="brand" size="xs">
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="light" color="gray" size="xs">
                      Unverified
                    </Badge>
                  )}
                </Group>
              ) : (
                '—'
              )
            }
          />
          <MetaPair
            label="Ways in"
            value={
              providers.length === 0 ? (
                '—'
              ) : (
                <Group gap={4}>
                  {providers.map((provider) => (
                    <Badge
                      key={provider}
                      variant="light"
                      color="gray"
                      size="sm"
                    >
                      {provider}
                    </Badge>
                  ))}
                </Group>
              )
            }
          />
          <MetaPair label="First seen" value={at(user.createdAt)} />
          <MetaPair label="Registered" value={at(user.registeredAt)} />
          <MetaPair label="Last sign-in" value={at(user.lastLoginAt)} />
        </Group>
      </Card>

      {user.lockedUntil && new Date(user.lockedUntil) > new Date() && (
        <Alert color="orange" title="Locked out">
          <Text size="sm">
            {user.failedLoginCount} consecutive failed password attempts. Unlocks
            at {at(user.lockedUntil)}. Per-account rather than per-IP, because an
            attacker has many addresses and this account has one.
          </Text>
        </Alert>
      )}

      <div className={classes.split}>
        <Stack gap="sm">
          <SectionHeading
            eyebrow="Timeline"
            title="What they did"
            subtitle="Ordered by the server clock."
            note="Recorded when the API received the fact, not when the device says it happened. A phone's clock is wrong and user-settable, so occurredAt is stored untrusted and nothing is ordered by it."
          />
          <Card>
            {user.events.length === 0 ? (
              <Text size="sm" c="dimmed">
                No events. A first run that never reached the API leaves none.
              </Text>
            ) : (
              <Timeline
                active={user.events.length}
                bulletSize={18}
                lineWidth={2}
              >
                {user.events.map((event) => (
                  <Timeline.Item key={event.id} title={event.type}>
                    <Text size="xs" c="dimmed">
                      {at(event.recordedAt)}
                    </Text>
                    {event.context &&
                      Object.keys(event.context).length > 0 && (
                        <Code block mt={6} className={classes.context}>
                          {JSON.stringify(event.context, null, 2)}
                        </Code>
                      )}
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Card>
        </Stack>

        <Stack gap="sm">
          <SectionHeading
            eyebrow="Sessions"
            title="Devices"
            note="User agent and IP address are client-supplied and recorded for audit only. They say what a request claimed, never where somebody was, and nothing authorises on them."
          />
          <Card>
            {user.sessions.length === 0 ? (
              <Text size="sm" c="dimmed">
                No sessions.
              </Text>
            ) : (
              <Stack gap="md">
                {user.sessions.map((session) => {
                  const dead =
                    session.revokedAt !== null ||
                    new Date(session.expiresAt) < new Date();

                  return (
                    <Stack key={session.id} gap={2}>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={600}>
                          {at(session.createdAt)}
                        </Text>
                        <Badge
                          variant="light"
                          color={dead ? 'gray' : 'brand'}
                          size="xs"
                        >
                          {session.revokedAt
                            ? 'Revoked'
                            : dead
                              ? 'Expired'
                              : 'Active'}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {session.userAgent ?? 'No user agent'} ·{' '}
                        {session.ipAddress ?? 'No address'}
                      </Text>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Card>
        </Stack>
      </div>

      <Stack gap="sm">
        <SectionHeading
          eyebrow="Act II"
          title="Conversations"
          subtitle="What ran, and how far it got."
          note="The transcript is not served here. conversation_turns is the first learner-authored free text in this database, and an operational screen does not need it — a turn count answers how far a scene got without turning this page into a reading surface."
        />
        <Card p={0}>
          {user.conversations.length === 0 ? (
            <Text size="sm" c="dimmed" p="lg">
              None. A learner on the bundled scene has no conversation row
              anywhere in this database, so this is empty for them too.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={560}>
              <Table verticalSpacing="sm" horizontalSpacing="lg">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={90}>Language</Table.Th>
                    <Table.Th w={130}>Status</Table.Th>
                    <Table.Th w={80} className={classes.numeric}>
                      Turns
                    </Table.Th>
                    <Table.Th w={90}>Band</Table.Th>
                    <Table.Th>Started</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {user.conversations.map((conversation) => (
                    <Table.Tr key={conversation.id}>
                      <Table.Td>
                        <Badge variant="light" color="gray" size="sm">
                          {conversation.languageCode.toUpperCase()}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{conversation.status}</Text>
                        {conversation.endedReason && (
                          <Text size="xs" c="dimmed">
                            {conversation.endedReason}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td className={classes.numeric}>
                        <Text size="sm">{conversation.turns}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          size="sm"
                          c={conversation.estimateBand ? undefined : 'dimmed'}
                        >
                          {conversation.estimateBand ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{at(conversation.createdAt)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>
        <Text size="xs" c="dimmed">
          The band is bookkeeping and never crosses `/v1/app/*` —
          docs/product.md keeps CEFR underneath, so a learner is told a sentence
          and never a level.
        </Text>
      </Stack>
    </Stack>
  );
}
